import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { findAccess, history } from '../../../utils.jsx';
import DeleteIcon from '@mui/icons-material/Delete';
import { getSystemsAdmins, setSystemAccess } from '../../../actionCreators/admin';
import { update } from '../../../actionCreators/system';
import MovaticCustomModal from '../../../components/Modal/MovaticCustomModal';
import { ADMIN_PERMISSIONS, ADMIN_FLAG_TO_ROLE_NAME } from '../../../constants.js';
import { PERMISSION_FIELDS } from '../../../util/adminPermissions';
import { useSystemStore } from '../../../store/systemStore';
import CustomToast from '../../../components/Redesing/custom-toast';
import {
  assignSystemRoleMutation,
  removeAdminSystemRolesMutation,
  removeAdminFromSystemMutation,
} from '../../../hooks/admins/mutation/V2';
import { fetchAdminUserByEmail, AdminUserLookup } from '../../../hooks/admins/query/V2';
import PageContainer from '../../../components/Redesing/page-container';
import CardView from '../../../components/Redesing/card-view';
import { Stack } from '@mui/system';
import IconButtonMenu from '../../../components/Redesing/icon-button-menu';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CustomAsyncToast from '../../../components/Redesing/custom-async-toast';
import { updateAdmin } from '../../../api/admin';
import { useParams } from 'react-router-dom';
import useQueryString from '../../../hooks/queryParams/useQueryString';
import useSystemAdmins from '../../../hooks/system/query/useSystemAdmins';
import { IAdmin, IAuth } from '../../../ts/interface/admins.interface';
import PageContent from '../../../components/Redesing/page-content';
import { MovaticFieldGroupCheck } from '../../../components/Redesing/movatic-field-group';

interface AdminDetailProps {
  auth: IAuth;
  systemAccess: number;
  systemLoaded: boolean;
  setSystemAccess: (access: number) => void;
}

const AdminDetail: React.FC<AdminDetailProps> = ({
  auth,
  systemLoaded,
  setSystemAccess,
  systemAccess,
}) => {
  const { email } = useParams<{ email: string }>();
  const systemGid = useSystemStore((s) => s.systemInfo.gid);
  const [showEdit, setShowEdit] = useState(false);
  const [adminObj, setAdminObj] = useState<any>({}); // Define the correct type for adminObj
  const [confirm, setConfirm] = useState(false);
  // Additive GraphQL lookup: admin's GraphQL id + active system role rows by name.
  // Kept in a ref for the synchronous save/diff path and in state for rendering.
  const adminLookupRef = useRef<AdminUserLookup | null>(null);
  const [graphqlLookup, setGraphqlLookup] = useState<AdminUserLookup | null>(null);
  // True until the admin's GraphQL system roles have first finished loading.
  const [rolesLoading, setRolesLoading] = useState(true);
  // Snapshot of `access` captured when entering edit mode, to diff on save.
  const preEditAccessRef = useRef<number | undefined>(undefined);
  const adminEditPermission = findAccess(systemAccess).includes('admin');
  const [currentTabV, setCurrentTabV] = useState('general');
  const handleTabsChange = useCallback(
    (event: React.SyntheticEvent, value: React.SetStateAction<string>) => {
      setCurrentTabV(value);
    },
    []
  );

  const { data: allAdmins = [] } = useSystemAdmins({
    enabled: systemLoaded,
  });

  useEffect(() => {
    const admin = allAdmins.find((admin: IAdmin) => admin.email === email);
    const adminAccess = findAccess(admin?.access);
    adminAccess.forEach((eachAccess: string) => {
      admin[eachAccess] = true;
    });
    setAdminObj(admin);
  }, [allAdmins, email]);

  // Additive GraphQL: resolve the admin's GraphQL id and active system role rows
  // so we can grant/soft-revoke roles alongside the REST permission update.
  const refreshAdminLookup = useCallback(
    async (showLoading: boolean = true) => {
      if (!email || !systemGid) return;
      if (showLoading) setRolesLoading(true);
      try {
        const lookup = await fetchAdminUserByEmail(email, systemGid);
        adminLookupRef.current = lookup;
        setGraphqlLookup(lookup);
      } catch {
        adminLookupRef.current = null;
        setGraphqlLookup(null);
      } finally {
        if (showLoading) setRolesLoading(false);
      }
    },
    [email, systemGid]
  );

  useEffect(() => {
    refreshAdminLookup();
  }, [refreshAdminLookup]);

  // Bitwise `access` derived from the admin's active GraphQL roles. This drives
  // the permission checkboxes so the UI reflects the new role system rather than
  // the legacy REST `access` integer. Undefined until the lookup resolves.
  const graphqlAccess = useMemo(() => {
    if (!graphqlLookup) return undefined;
    let access = 1;
    Object.entries(ADMIN_FLAG_TO_ROLE_NAME).forEach(([flag, roleName]) => {
      if (graphqlLookup.roleRowsByName[roleName as string]) {
        access += ADMIN_PERMISSIONS[flag as keyof typeof ADMIN_PERMISSIONS];
      }
    });
    return access;
  }, [graphqlLookup]);

  // In view mode the checkboxes reflect GraphQL roles; while editing they reflect
  // the in-progress bitwise edits (seeded from GraphQL roles in startEdit).
  const displayAccess = showEdit ? adminObj?.access : graphqlAccess ?? adminObj?.access;

  // Diff pre-edit vs saved access and mirror the change as GraphQL role
  // grants / removals. REST remains authoritative — failures only warn. The
  // caller (save) turns the spinner on before this runs; we clear it on every
  // exit path so the card shows loading from Save until the roles are re-fetched.
  const syncRolesToGraphQL = useCallback(
    async (originalAccess: number | undefined, newAccess: number | undefined) => {
      try {
        // Refresh first so we revoke against the admin's current role rows rather
        // than a stale snapshot.
        await refreshAdminLookup(false);
        const lookup = adminLookupRef.current;
        if (!lookup || !systemGid) return;

        const originalFlags = new Set(findAccess(originalAccess));
        const newFlags = new Set(findAccess(newAccess));

        const calls: Promise<{ isSuccess: boolean }>[] = [];
        const rolesToRemove: string[] = [];

        Object.entries(ADMIN_FLAG_TO_ROLE_NAME).forEach(([flag, roleName]) => {
          const had = originalFlags.has(flag);
          const has = newFlags.has(flag);
          if (has && !had) {
            calls.push(
              assignSystemRoleMutation({
                adminId: lookup.adminId,
                systemId: systemGid,
                roleName: roleName as string,
              })
            );
          } else if (!has && had) {
            rolesToRemove.push(roleName as string);
          }
        });

        // Remove all revoked roles in a single bulk mutation.
        if (rolesToRemove.length > 0) {
          calls.push(
            removeAdminSystemRolesMutation({
              adminId: lookup.adminId,
              systemId: systemGid,
              roleNames: rolesToRemove,
            })
          );
        }

        if (calls.length === 0) return;

        const results = await Promise.allSettled(calls);
        const anyFailed = results.some(
          (r) => r.status === 'rejected' || !r.value.isSuccess
        );
        if (anyFailed) {
          CustomToast({
            type: 'warning',
            message: 'Permissions saved, but syncing roles to the new system failed.',
          });
        }
        // Refresh so the checkboxes reflect the updated roles.
        await refreshAdminLookup(false);
      } finally {
        setRolesLoading(false);
      }
    },
    [systemGid, refreshAdminLookup]
  );

  const warning = useMemo(() => {
    if (!showEdit && !rolesLoading && displayAccess === 1) {
      return 'This user has no permissions';
    }
    return undefined;
  }, [displayAccess, showEdit, rolesLoading]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const adminCopy = { ...adminObj };
    const value =
      event.target.type === 'checkbox' ? event.target.checked : event.target.value;

    switch (event.target.name) {
      case 'admin':
        if (value) {
          adminCopy.access = ADMIN_PERMISSIONS['full'];
        } else {
          adminCopy.access -= ADMIN_PERMISSIONS['admin'];
        }
        break;
      case 'rental':
        if (value) {
          adminCopy.access += ADMIN_PERMISSIONS['rental'];
        } else {
          adminCopy.access -= ADMIN_PERMISSIONS['rental'];

          if (adminCopy.access & ADMIN_PERMISSIONS['admin']) {
            adminCopy.access -= ADMIN_PERMISSIONS['admin'];
          }
        }
        break;
      case 'rates':
        if (value) {
          adminCopy.access += ADMIN_PERMISSIONS['rates'];
        } else {
          adminCopy.access -= ADMIN_PERMISSIONS['rates'];

          if (adminCopy.access & ADMIN_PERMISSIONS['admin']) {
            adminCopy.access -= ADMIN_PERMISSIONS['admin'];
          }
        }
        break;
      case 'refunds':
        if (value) {
          adminCopy.access += ADMIN_PERMISSIONS['refunds'];
        } else {
          adminCopy.access -= ADMIN_PERMISSIONS['refunds'];

          if (adminCopy.access & ADMIN_PERMISSIONS['admin']) {
            adminCopy.access -= ADMIN_PERMISSIONS['admin'];
          }
        }
        break;
      case 'bank':
        if (value) {
          adminCopy.access += ADMIN_PERMISSIONS['bank'];
        } else {
          adminCopy.access -= ADMIN_PERMISSIONS['bank'];

          if (adminCopy.access & ADMIN_PERMISSIONS['admin']) {
            adminCopy.access -= ADMIN_PERMISSIONS['admin'];
          }
        }
        break;
      case 'physical':
        if (value) {
          adminCopy.access += ADMIN_PERMISSIONS['physical'];
        } else {
          adminCopy.access -= ADMIN_PERMISSIONS['physical'];

          if (adminCopy.access & ADMIN_PERMISSIONS['admin']) {
            adminCopy.access -= ADMIN_PERMISSIONS['admin'];
          }
        }
        break;
      case 'users':
        if (value) {
          adminCopy.access += ADMIN_PERMISSIONS['users'];
        } else {
          adminCopy.access -= ADMIN_PERMISSIONS['users'];

          if (adminCopy.access & ADMIN_PERMISSIONS['admin']) {
            adminCopy.access -= ADMIN_PERMISSIONS['admin'];
          }
        }
        break;
      case 'locations':
        if (value) {
          adminCopy.access += ADMIN_PERMISSIONS['locations'];
        } else {
          adminCopy.access -= ADMIN_PERMISSIONS['locations'];

          if (adminCopy.access & ADMIN_PERMISSIONS['admin']) {
            adminCopy.access -= ADMIN_PERMISSIONS['admin'];
          }
        }
        break;
      case 'support':
        if (value) {
          adminCopy.access += ADMIN_PERMISSIONS['support'];
        } else {
          adminCopy.access -= ADMIN_PERMISSIONS['support'];

          if (adminCopy.access & ADMIN_PERMISSIONS['admin']) {
            adminCopy.access -= ADMIN_PERMISSIONS['admin'];
          }
        }
        break;
      case 'maintenance':
        if (value) {
          adminCopy.access += ADMIN_PERMISSIONS['maintenance'];
        } else {
          adminCopy.access -= ADMIN_PERMISSIONS['maintenance'];

          if (adminCopy.access & ADMIN_PERMISSIONS['admin']) {
            adminCopy.access -= ADMIN_PERMISSIONS['admin'];
          }
        }
        break;
      case 'memberships':
        if (value) {
          adminCopy.access += ADMIN_PERMISSIONS['memberships'];
        } else {
          adminCopy.access -= ADMIN_PERMISSIONS['memberships'];

          if (adminCopy.access & ADMIN_PERMISSIONS['admin']) {
            adminCopy.access -= ADMIN_PERMISSIONS['admin'];
          }
        }
        break;
      case 'report':
        adminCopy.error_report_emails = value;
        break;
      case 'pending':
        adminCopy.pending_user_emails = value;
        break;
    }

    setAdminObj(adminCopy);
  };

  const startEdit = () => {
    // Seed the editable bitwise model from the GraphQL roles (falling back to the
    // REST access) so toggles and the save diff operate on the role-derived state.
    const base = graphqlAccess ?? adminObj?.access;
    setAdminObj({ ...adminObj, access: base });
    preEditAccessRef.current = base;
    setShowEdit(true);
  };

  // Arriving with ?edit=1 (the Edit action on the admins list) opens edit mode
  // once the roles are loaded, honoring the same gate as the Edit button.
  const wantsEdit = useQueryString().queryParams.get('edit') === '1';
  const autoEditDoneRef = useRef(false);
  useEffect(() => {
    if (
      wantsEdit &&
      !autoEditDoneRef.current &&
      !rolesLoading &&
      adminObj?.email &&
      (adminEditPermission || auth.admin.movaticAccess)
    ) {
      autoEditDoneRef.current = true;
      startEdit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsEdit, rolesLoading, adminObj]);

  const saveAccess = () => {
    const adminCopy = { ...adminObj };
    save(adminCopy);
  };

  // Remove the admin from the current system via GraphQL, revoking every role and
  // permission they hold there. Replaces the legacy REST `archive` flow
  // (`updateAdmin({ archive: true })`). The mutation resolves with a result
  // envelope rather than rejecting, so we throw on failure to drive the toast's
  // error state and skip navigation.
  const removeAdmin = () => {
    setConfirm(false);
    CustomAsyncToast({
      promise: () =>
        removeAdminFromSystemMutation({
          email: adminObj.email,
          systemId: systemGid,
        }).then((res) => {
          if (!res.isSuccess) {
            throw new Error(res.errors?.[0]?.message || 'Failed to remove admin');
          }
          return res;
        }),
      successMessage: () => 'Admin removed',
      errorMessage: 'There was an error removing the admin',
      loadingMessage: 'Removing admin...',
    }).then((success) => {
      if (success) {
        history.push('/settings/admin');
      }
    });
  };

  const save = (adminObj: any) => {
    setShowEdit(!showEdit);
    // Show the loader immediately so we don't briefly render the stale role
    // toggles while REST updates and roles re-sync.
    setRolesLoading(true);
    CustomAsyncToast({
      promise: () => updateAdmin(adminObj),
      successMessage: () => {
        if (auth.admin.email === adminObj.email) {
          setSystemAccess(adminObj.access);
        }
        return 'Admin updated';
      },
      errorMessage: 'There was an error updating the admin',
      loadingMessage: 'Updating admin...',
    }).then((success) => {
      if (success) {
        // Additive GraphQL: mirror the permission edit as role grants/revokes.
        // syncRolesToGraphQL clears the loader once roles are re-fetched.
        syncRolesToGraphQL(preEditAccessRef.current, adminObj.access);
      } else {
        // REST update failed; no sync will run, so clear the loader.
        setRolesLoading(false);
      }
    });
  };

  const button = () => {
    if (showEdit) {
      return (
        <>
          <IconButtonMenu
            buttons={[
              {
                label: 'Cancel',
                onClick: () => setShowEdit(!showEdit),
                disabled: !adminEditPermission && !auth.admin.movaticAccess,
              },
              {
                label: 'Save',
                onClick: () => saveAccess(),
                disabled: !adminEditPermission && !auth.admin.movaticAccess,
                startIcon: <SaveIcon />,
              },
            ]}
          />
        </>
      );
    }

    return (
      <>
        <IconButtonMenu
          buttons={[
            {
              label: 'Edit',
              onClick: () => startEdit(),
              disabled: !adminEditPermission && !auth.admin.movaticAccess,
              startIcon: <EditIcon />,
            },
            {
              label: 'Remove',
              onClick: () => setConfirm(!confirm),
              disabled: !adminEditPermission && !auth.admin.movaticAccess,
              startIcon: <DeleteIcon />,
            },
          ]}
        />
      </>
    );
  };

  const content = () => {
    // View mode renders GraphQL-role state; edit mode renders in-progress edits.
    const access = displayAccess;
    const permissions = PERMISSION_FIELDS.map((field) => ({
      ...field,
      checked: Boolean(access & ADMIN_PERMISSIONS[field.name]),
    }));
    return (
      <Stack spacing={2}>
        <CardView
          content={
            <Stack px={2} pb={2}>
              {permissions.map((permission) => (
                <MovaticFieldGroupCheck
                  key={permission.name}
                  id={permission.name}
                  label={permission.label}
                  description={permission.description}
                  onChange={handleChange}
                  checked={permission.checked}
                  disabled={!showEdit}
                  name={permission.name}
                />
              ))}
            </Stack>
          }
          title={'Admin Permissions'}
          isLoading={rolesLoading}
        />
      </Stack>
    );
  };

  const contentNotifications = () => {
    const { pending_user_emails, error_report_emails } = adminObj || {};
    const emailNotifications = [
      {
        label: 'Pending Users',
        description: 'Receive notifications for new pending users',
        name: 'pending',
        checked: Boolean(pending_user_emails),
      },
      {
        label: 'Problem Reports',
        description: 'Receive notifications for new problem reports submitted by users',
        name: 'report',
        checked: Boolean(error_report_emails),
      },
    ];

    return (
      <Stack spacing={2}>
        <CardView
          content={
            <Stack px={2} pb={2}>
              {emailNotifications.map((permission) => (
                <MovaticFieldGroupCheck
                  key={permission.name}
                  id={permission.name}
                  label={permission.label}
                  description={permission.description}
                  onChange={handleChange}
                  checked={permission.checked}
                  disabled={!showEdit}
                  name={permission.name}
                />
              ))}
            </Stack>
          }
          title={'Email Membership Notifications'}
        />
      </Stack>
    );
  };

  const modal = () => {
    return (
      <>
        <MovaticCustomModal
          title={'Remove Admin'}
          open={confirm}
          onClose={() => setConfirm(!confirm)}
          content={<div>Are you sure you would like to remove this admin?</div>}
          action={
            <IconButtonMenu
              buttons={[
                {
                  label: 'Cancel',
                  onClick: () => setConfirm(!confirm),
                  disabled: !adminEditPermission && !auth.admin.movaticAccess,
                },
                {
                  label: 'Remove',
                  onClick: () => removeAdmin(),
                  disabled: !adminEditPermission && !auth.admin.movaticAccess,
                  startIcon: <DeleteIcon />,
                },
              ]}
            />
          }
        />
      </>
    );
  };

  return (
    <PageContainer warningMessage={warning} isLoading={!systemLoaded}>
      <PageContent
        backLink={'/settings/admin'}
        contentTitle={'System Admins'}
        itemName={`${adminObj?.firstName} ${adminObj?.lastName}`}
        itemChipValue={adminObj?.email}
        chipLabel={'Admin Email'}
        currentTabV={currentTabV}
        itemTopButtons={button()}
        handleTabsChange={handleTabsChange}
        tabsList={[
          { label: 'General', value: 'general' },
          { label: 'Notifications', value: 'notifications' },
        ]}
        tabsChildren={
          <>
            {currentTabV === 'general' && <>{content()}</>}
            {currentTabV === 'notifications' && <>{contentNotifications()}</>}
          </>
        }
      />
      {modal()}
    </PageContainer>
  );
};

export default connect(
  (state: any) => ({
    auth: state.admin.admin,
    systemAccess: state.admin.systemAccess,
    system: state.system.current,
    systemLoaded: state.system.isLoaded,
    admins: state.admin.systemAdmins,
  }),
  (dispatch: Function) => ({
    update(loading: boolean) {
      dispatch(update(loading));
    },
    getSystemsAdmins: () => {
      dispatch(getSystemsAdmins());
    },
    setSystemAccess: (access: number) => {
      dispatch(setSystemAccess(access));
    },
  })
)(AdminDetail);
